/**
 * Tema visual de cada juego — DATO, no código.
 *
 * Lo consumen dos piezas: el carrusel del hero (`CarruselJuegos`) y la vista
 * de extracto por juego (`ExtractosJuegos`). Las dos comparten la misma
 * regla y el mismo archivo, para que un juego se vista una sola vez.
 *
 * Regla de la pieza: **la diapositiva cambia de vestido, no de casa.** El
 * marco (la caja del hero, su anillo turquesa, el ritmo, el espaciado, el
 * link del pie, la fila de navegación) es de INPROJUY y no se mueve. Lo que
 * se viste es el contenido: el logo, un acento de color y —donde el juego
 * publique una— su tipografía.
 *
 * Para sumar un juego nuevo se agrega una entrada acá y nada más:
 * `CarruselJuegos.jsx` y `useCarruselJuegos.js` no se tocan. Un juego sin
 * entrada cae a `TEMA_CASA` (el dorado de INPROJUY y el logo homónimo de
 * `public/img/juegos/`), que se ve bien igual — nunca roto.
 *
 * ⚠️ PERMISOS DE MARCA. Los logos, colores y tipografías de acá son la
 * identidad registrada de terceros (LOTBA, Lotería de Santa Fe, Lotería
 * Jujeña). Usarlos en el sitio de INPROJUY necesita **aval institucional**;
 * es una cuestión de permisos, no técnica, y la decide INPROJUY. Este archivo
 * deja el trabajo listo, no lo autoriza.
 *
 * ── Campos ────────────────────────────────────────────────────────────────
 *  logo    nombre del archivo dentro de `public/img/juegos/`
 *  acento  color de marca. Uso SOLO decorativo (el velo de luz detrás del
 *          logo, el filete del borde inferior). Nunca lleva texto encima, así
 *          que no le aplica el mínimo de contraste.
 *  tinta   versión del acento corregida para ser legible. Es la que lleva
 *          texto: el monto sobre la tarjeta y la etiqueta (tinta de fondo con
 *          `--ink` encima). Las dos relaciones están verificadas abajo.
 *  fuente  familia del juego para el monto y el número. `null` = la display
 *          de INPROJUY. Solo se declara cuando el sitio oficial del juego
 *          publica esa familia y está en Google Fonts: no se le inventa una
 *          tipografía a un juego que no la declaró.
 *  esc     ajuste óptico del alto del logo. Un sello redondo a la misma
 *          altura que un logotipo apaisado se lee más chico; los redondos
 *          van a 1.14.
 *  plato   `true` cuando el logotipo oficial está especificado para fondo
 *          claro y pierde partes sobre la tarjeta oscura. Lo lleva solo Loto
 *          5 Plus: su "PLUS" es #2D2A26 y desaparece. Se lo verificó en
 *          pantalla juego por juego; los otros cinco se leen sin respaldo.
 *
 * ── Campos que agregó la vista de extracto por juego (20/08/2026) ─────────
 *
 *  tintaAAA  la tinta que alcanza **7:1 (AAA)** sobre el panel del extracto
 *            (#0F2A32). En cinco de los seis juegos es el MISMO valor que
 *            `tinta` —ya lo alcanzaba— y por eso `temaDe()` cae a `tinta`
 *            cuando no está declarado. El único que difiere es Loto Plus:
 *            su #FF6B5E da 5,38 (AA pero no AAA) y el extracto es una pieza
 *            de texto chico y denso, donde la vara de este vertical es AAA.
 *            No se tocó `tinta`: el carrusel la usa para el monto, que es
 *            texto grande, y cambiarla movería una pieza que nadie pidió.
 *
 *  realce    SEGUNDO color de marca, cuando el juego publica uno y lo usa
 *            para una pieza distinta de las bolillas del bolillero. Se
 *            declara con su tinta ya legible, porque acá sí lleva texto:
 *            es el relleno de una bolilla con el número encima.
 *              · lotoplus → #FFCC00, el amarillo de la esfera del Número
 *                PLUS. Tomado de `loto.loteriadelaciudad.gob.ar/css/
 *                resultados.css` → `.plus-circle-display{background:#ffcc00;
 *                color:#000}`. Contraste 9,94 sobre el panel y 11,89 con
 *                `--ink` encima.
 *              · pozo → #7CD4FD, la Super Bola. La fuente la pinta de azul:
 *                `functions/extractos/parser.js:167` la reconoce porque su
 *                clase contiene "blue". El tono se aclaró hasta 9,08 sobre
 *                el panel (10,87 con `--ink` encima); el azul saturado del
 *                origen no llega ni a AA sobre fondo oscuro.
 *
 *  ficha     forma del casillero del número, **como la dibuja el sitio del
 *            propio juego**. Es la diferencia de vestido más visible entre
 *            las dos casas, y no es decorativa: es lo que hace que el
 *            extracto de Quini 6 se vea de Quini 6.
 *              'circulo'  bolilla redonda plena
 *                         → loto: `.bolilla-blanca{border-radius:50%}`
 *              'anillo'   bolilla redonda con contorno oscuro
 *                         → loto5: `.resultado .item-loto p{border-radius:
 *                           50%; border:solid 2px #000}`
 *              'cuadrado' casillero cuadrado, sin redondeo
 *                         → Santa Fe: `.cuadrado{}` de
 *                           `apps.loteriasantafe.gov.ar:8443/Extractos/
 *                           paginas/css/generalvd1.css` (Quini 6 y Brinco)
 *            Sin declarar, 'circulo'.
 *
 * ── Contraste verificado (WCAG 2.1, no estimado) ──────────────────────────
 * Fondo de referencia #0F2A32, el punto más claro del degradado de la
 * tarjeta; tinta oscura de referencia #08191E (`--ink`).
 *
 *   juego       tinta      tinta / tarjeta    ink / tinta
 *   lotoplus    #FF6B5E         5.38               6.43
 *   loto5plus   #2ED27A         7.60               9.09
 *   quini6      #3FCDEC         7.98               9.55
 *   brinco      #FFE23D        11.59              13.87
 *   pozo        #D9E64A        11.00              13.16
 *   tombola     #FFA733         7.75               9.27
 *   (casa)      #F4B740         8.37              10.01
 *
 * Todas pasan AA para texto normal (4.5:1). El color de marca crudo NO pasa
 * —#E1251F da 3.21 y #D8442A da 3.42— y por eso existe la separación entre
 * `acento` y `tinta`: el rojo de Loto Plus vive en el velo, no en el texto.
 *
 * Sobre el panel del extracto (#0F2A32), con la vara AAA de 7:1:
 *
 *   juego       tintaAAA   tintaAAA / panel   ink / tintaAAA
 *   lotoplus    #FF9C90         7.45               8.91
 *   loto5plus   #2ED27A         7.60               9.09
 *   quini6      #3FCDEC         7.98               9.55
 *   brinco      #FFE23D        11.59              13.87
 *   pozo        #D9E64A        11.00              13.16
 *   tombola     #FFA733         7.75               9.27
 *   (casa)      #F4B740         8.37              10.01
 *
 *   realce      lotoplus #FFCC00  9.94 / 11.89   ·  pozo #7CD4FD  9.08 / 10.87
 *   bolilla     #FFFFFF con `--ink` encima: 17.98
 *   crema #F7F5F0 sobre el panel: 13.79 · apagado #A9BAB7: 7.44
 *
 * ── De dónde salió cada cosa ──────────────────────────────────────────────
 *  lotoplus   logo  loto.loteriadelaciudad.gob.ar/images/logos/logo-LOTO-plus.svg
 *             color #E1251F, tomado del propio SVG
 *             fuente Anton, del <link> a Google Fonts de ese sitio
 *  loto5plus  logo  loto5.loteriadelaciudad.gob.ar/images/logoLoto5.svg
 *                   (se le quitó un <image> base64 —una sombra al 8%— que era
 *                   151 KB de los 156 KB del archivo; quedó vectorial, 5 KB)
 *             color #00953B, del propio SVG. El rojo también es suyo, pero ya
 *                   lo tiene Loto Plus: el verde separa a los dos hermanos
 *             fuente Anton, misma casa (LOTBA)
 *  quini6     logo  loterialajujeña.com.ar/img/extractos/resources/quini6-logo.png
 *             color #D8442A y el cian #00A8CC del "6", muestreados del logo
 *             fuente ninguna — loteriasantafe.gov.ar no publica webfont
 *  brinco     logo  loterialajujeña.com.ar/img/extractos/resources/brinco-logo.png
 *             color #303084 (el escudo) y #FCD80C (el logotipo)
 *             fuente ninguna — mismo motivo que Quini 6
 *  pozo       logo  loterialajujeña.com.ar/img/extractos/resources/pozo-logo.png
 *             color #D8D800, el disco
 *             fuente Oswald, declarada en loterialajujeña.com.ar/css/fonts.css
 *  tombola    logo  loterialajujeña.com.ar/img/imputacion/tombola-jujuy.png
 *                   (el `tombola-logo.png` del mismo sitio es un lettering
 *                    oscuro que desaparece sobre la tarjeta; este sello no)
 *             color #E49000, muestreado del sello
 *             fuente Oswald, misma casa
 *
 * Los PNG se bajaron del sitio oficial, se recortaron al borde del dibujo y
 * se cuantizaron a 128 colores: los seis logos juntos pesan 108 KB.
 */

/** Se aplica a todo juego sin entrada propia. Es el vestido de la casa. */
export const TEMA_CASA = {
  logo: null,
  acento: '#F4B740',
  tinta: '#F4B740',
  fuente: null,
  esc: 1,
  plato: false,
};

export const TEMAS = {
  lotoplus: {
    logo: 'lotoplus-oficial.svg',
    acento: '#E1251F',
    tinta: '#FF6B5E',
    // #FF6B5E da 5,38 sobre el panel del extracto: AA, no AAA. Es el único
    // juego donde las dos tintas difieren.
    tintaAAA: '#FF9C90',
    realce: '#FFCC00',
    ficha: 'circulo',
    fuente: '"Anton", var(--display)',
    esc: 1,
  },
  loto5plus: {
    logo: 'loto5plus-oficial.svg',
    acento: '#00953B',
    tinta: '#2ED27A',
    ficha: 'anillo',
    fuente: '"Anton", var(--display)',
    esc: 1,
    plato: true,
  },
  quini6: {
    logo: 'quini6-oficial.png',
    acento: '#D8442A',
    tinta: '#3FCDEC',
    ficha: 'cuadrado',
    fuente: null,
    esc: 1,
  },
  brinco: {
    logo: 'brinco-oficial.png',
    acento: '#303084',
    tinta: '#FFE23D',
    ficha: 'cuadrado',
    fuente: null,
    esc: 1.14,
  },
  pozo: {
    logo: 'pozo-oficial.png',
    acento: '#D8D800',
    tinta: '#D9E64A',
    realce: '#7CD4FD',
    ficha: 'circulo',
    fuente: '"Oswald", var(--display)',
    esc: 1.14,
  },
  tombola: {
    logo: 'tombola-oficial.png',
    acento: '#E49000',
    tinta: '#FFA733',
    ficha: 'circulo',
    fuente: '"Oswald", var(--display)',
    esc: 1.14,
  },
};

/**
 * Devuelve el tema de un juego con la URL del logo ya resuelta.
 *
 * Sin entrada declarada cae al tema de la casa y al logo homónimo que ya
 * estaba en `public/img/juegos/{clave}.png` — el mismo que usaba el carrusel
 * antes de este cambio. Un juego nuevo entra al hero sin verse roto aunque
 * nadie le haya hecho el relevamiento de identidad todavía.
 */
export function temaDe(clave, publicUrl = '') {
  const tema = TEMAS[clave] || TEMA_CASA;
  const archivo = tema.logo || `${clave}.png`;
  return {
    acento: tema.acento,
    tinta: tema.tinta,
    // Cae a `tinta` porque en cinco de los seis juegos son el mismo valor: se
    // declara solo la excepción, no la repetición.
    tintaAAA: tema.tintaAAA || tema.tinta,
    realce: tema.realce || null,
    ficha: tema.ficha || 'circulo',
    fuente: tema.fuente,
    esc: tema.esc,
    plato: !!tema.plato,
    logo: `${publicUrl}/img/juegos/${archivo}`,
  };
}
